import { configureStore, combineReducers } from '@reduxjs/toolkit';
import { setupListeners } from '@reduxjs/toolkit/query';
import { persistStore, persistReducer, FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import { baseApi } from './api/baseApi';
import { ownerBaseApi } from './api/ownerBaseApi';
import { bidderBaseApi } from './api/bidderBaseApi';
import { publicGustavoApi } from './api/publicGustavoApi';
import { analyticsMiddleware } from './analyticsMiddleware';
import authReducer from '../features/auth/authSlice';
import snackbarReducer from './snackbarSlice';
import { setPersistorRef } from './persistorRef';

const persistConfig = {
  key: 'imperium-v2',
  version: 1,
  storage,
  whitelist: ['auth'], // solo persistir auth, no el caché de la API
};

const rootReducer = combineReducers({
  auth: authReducer,
  snackbar: snackbarReducer,
  [baseApi.reducerPath]:          baseApi.reducer,
  [ownerBaseApi.reducerPath]:     ownerBaseApi.reducer,
  [bidderBaseApi.reducerPath]:    bidderBaseApi.reducer,
  [publicGustavoApi.reducerPath]: publicGustavoApi.reducer,
});

const persistedReducer = persistReducer(persistConfig, rootReducer);

export const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    }).concat(
      baseApi.middleware,
      ownerBaseApi.middleware,
      bidderBaseApi.middleware,
      publicGustavoApi.middleware,
      analyticsMiddleware,
    ),
});

export const persistor = persistStore(store);
setPersistorRef(persistor);

// Habilita `refetchOnFocus` / `refetchOnReconnect` en las queries que lo pidan. Sin
// esto esas opciones se ignoran en silencio: RTK Query no escucha los eventos del
// navegador hasta que alguien los engancha. Solo actúa donde se declara — no cambia
// el comportamiento de las queries que no lo piden.
setupListeners(store.dispatch);

export type RootState = ReturnType<typeof rootReducer>;
export type AppDispatch = typeof store.dispatch;
